import React, { useEffect, useRef, useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';

import DefaultBPIcon from '../../../../../assets/icons/DefaultBPIcon';
import {
  ActiveBPWrapper,
  ArrowIconWrapper,
  BPDetailsWrapper,
  BPItem,
  EndAoBWrapper,
  FullWidthDiv,
  IconWrapper,
} from '../styles';
import { BusinessPartnerSelectorItem } from '../types';
import { useI18n } from '../../../../../hooks/useI18n';
import { TranslationKeys } from '../../../../../i18n/locales/defaultResources';
import ActionButton from '../../../../components/ActionButton/ActionButton';

export type ActiveBusinessPartnerDisplayProps = {
  selectedBusinessPartner?: BusinessPartnerSelectorItem;
  onClick?: () => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  isListOpen: boolean;
  aobActive?: boolean;
  onAobEnd: () => void;
};

const ActiveBusinessPartnerDisplay: React.FC<
  ActiveBusinessPartnerDisplayProps
> = ({
  selectedBusinessPartner,
  onClick,
  onKeyDown,
  isListOpen,
  aobActive,
  onAobEnd,
}) => {
  const { t } = useI18n();
  const bpNameRef = useRef<HTMLSpanElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (bpNameRef.current) {
        setShowTooltip(bpNameRef.current.scrollHeight > 100);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);

    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, [selectedBusinessPartner]);

  const renderEmpty = () => (
    <ActiveBPWrapper
      onClick={onClick}
      onKeyDown={onKeyDown}
      style={{ cursor: 'pointer', width: '100%' }}
      tabIndex={0}
    >
      <BPItem>
        <BPDetailsWrapper>
          <span>{t(TranslationKeys.businessPartner_select_bp_number)}</span>
        </BPDetailsWrapper>
        <ArrowIconWrapper>
          <IconButton>
            {isListOpen ? (
              <ArrowDropUpIcon style={{ color: 'white' }} />
            ) : (
              <ArrowDropDownIcon style={{ color: 'white' }} />
            )}
          </IconButton>
        </ArrowIconWrapper>
      </BPItem>
    </ActiveBPWrapper>
  );

  const renderWithBpInfo = () => {
    const bpNameElement = showTooltip ? (
      <Tooltip
        title={selectedBusinessPartner?.businessPartnerDisplayName}
        arrow
        PopperProps={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [-15, 0],
              },
            },
          ],
        }}
      >
        <span ref={bpNameRef} className="bp-name">
          {selectedBusinessPartner?.businessPartnerDisplayName}
        </span>
      </Tooltip>
    ) : (
      <span ref={bpNameRef} className="bp-name">
        {selectedBusinessPartner?.businessPartnerDisplayName}
      </span>
    );

    return (
      <FullWidthDiv>
        <ActiveBPWrapper
          onClick={onClick}
          onKeyDown={onKeyDown}
          style={{ cursor: 'pointer', width: '100%' }}
          tabIndex={0}
        >
          <BPItem>
            {selectedBusinessPartner && (
              <IconWrapper>
                <DefaultBPIcon />
              </IconWrapper>
            )}
            <BPDetailsWrapper>
              <span className="bp-number">
                {selectedBusinessPartner?.businessPartnerNumber}
              </span>
              {bpNameElement}
            </BPDetailsWrapper>
            <ArrowIconWrapper>
              <IconButton>
                {isListOpen ? (
                  <ArrowDropUpIcon style={{ color: 'white' }} />
                ) : (
                  <ArrowDropDownIcon style={{ color: 'white' }} />
                )}
              </IconButton>
            </ArrowIconWrapper>
          </BPItem>
        </ActiveBPWrapper>
        {aobActive && (
          <EndAoBWrapper style={{ display: isListOpen ? 'block' : 'none' }}>
            <ActionButton
              variant="abort"
              buttonText={t(TranslationKeys.end_aob_button)}
              onClickAction={onAobEnd}
              fullWidth
            />
          </EndAoBWrapper>
        )}
      </FullWidthDiv>
    );
  };

  return (
    <FullWidthDiv>
      {selectedBusinessPartner ? renderWithBpInfo() : renderEmpty()}
    </FullWidthDiv>
  );
};

export default ActiveBusinessPartnerDisplay;
